import React, { useState } from 'react';
import { Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { Message } from '../types';

interface TurnUsage {
  turn: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  contextPercent: number;
  peakAgent: string;
}

function aggregateFromMessages(messages: Message[]) {
  const turns: TurnUsage[] = [];
  let totalTokens = 0;
  let peakContextPercent = 0;
  let turnIndex = 0;

  for (const msg of messages) {
    if (msg.sender !== 'assistant' || !msg.tokenUsage) continue;
    turnIndex++;
    const tu = msg.tokenUsage;
    totalTokens += tu.totalTokens;
    if (tu.contextUsagePercent > peakContextPercent) {
      peakContextPercent = tu.contextUsagePercent;
    }
    turns.push({
      turn: turnIndex,
      totalTokens: tu.totalTokens,
      promptTokens: tu.totalPromptTokens,
      completionTokens: tu.totalCompletionTokens,
      contextPercent: tu.contextUsagePercent,
      peakAgent: tu.peakPromptAgent,
    });
  }

  return { turns, totalTokens, peakContextPercent, turnCount: turnIndex };
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

interface SessionTokenBarProps {
  messages: Message[];
}

export function SessionTokenBar({ messages }: SessionTokenBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { turns, totalTokens, peakContextPercent, turnCount } = aggregateFromMessages(messages);

  if (turnCount === 0) return null;

  return (
    <div
      className="border-t border-gray-100 bg-gray-50 text-xs text-gray-500"
      data-testid="session-token-bar"
    >
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-1.5 hover:bg-gray-100 transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <Zap className="h-3 w-3 text-amber-500" />
          <span className="font-medium text-gray-600">
            {formatTokens(totalTokens)} tokens
          </span>
          <span className="text-gray-400">·</span>
          <span>{turnCount} {turnCount === 1 ? 'turn' : 'turns'}</span>
          <span className="text-gray-400">·</span>
          <span>Peak context: {peakContextPercent}%</span>
        </span>
        {isExpanded
          ? <ChevronDown className="h-3 w-3 text-gray-400" />
          : <ChevronUp className="h-3 w-3 text-gray-400" />
        }
      </button>

      {isExpanded && (
        <div className="px-4 pb-2">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-gray-400 border-b border-gray-200">
                <th className="text-left py-1 font-medium">Turn</th>
                <th className="text-right py-1 font-medium">Prompt</th>
                <th className="text-right py-1 font-medium">Completion</th>
                <th className="text-right py-1 font-medium">Total</th>
                <th className="text-right py-1 font-medium">Context %</th>
                <th className="text-right py-1 font-medium">Peak Agent</th>
              </tr>
            </thead>
            <tbody>
              {turns.map((t) => (
                <tr key={t.turn} className="border-b border-gray-100 last:border-0">
                  <td className="py-1 text-gray-600">#{t.turn}</td>
                  <td className="py-1 text-right">{formatTokens(t.promptTokens)}</td>
                  <td className="py-1 text-right">{formatTokens(t.completionTokens)}</td>
                  <td className="py-1 text-right font-medium text-gray-600">{formatTokens(t.totalTokens)}</td>
                  <td className="py-1 text-right">{t.contextPercent}%</td>
                  <td className="py-1 text-right text-gray-400">{t.peakAgent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
